// src/pages/Admin/Cities/AdminStreets.jsx
import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/utils/axios";

// Reuse the same styles as AdminCities
import cls from "./AdminStreets.module.scss";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return "—";
  }
};

const AdminStreets = () => {
  const { cityId } = useParams();
  const [streets, setStreets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [cityMeta, setCityMeta] = useState(null);

  const fallbackImg = "https://dummyimage.com/120x80/1a1a1a/ffffff&text=Street";

  const getImg = (street) => {
    if (!street) return fallbackImg;

    // adminStreetController returns avatarThumb / avatar same shape as cities
    const thumbUrl = street.avatarThumb?.url;
    const avatarUrl = street.avatar?.url;

    return thumbUrl || avatarUrl || fallbackImg;
  };

  const fetchCityMeta = useCallback(async () => {
    if (!cityId) return;
    try {
      const { data } = await api.get(`/v1/admin/cities/${cityId}`);
      setCityMeta(data || null);
    } catch (e) {
      // not critical, just log
      console.error("Failed to load city meta", e);
    }
  }, [cityId]);

  const fetchStreets = useCallback(async () => {
    if (!cityId) return;
    try {
      setLoading(true);
      setErr("");
      const { data } = await api.get(`/v1/admin/cities/${cityId}/streets`);
      setStreets(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load streets", e);
      const msg =
        e?.response?.data?.message || e?.message || "Could not load streets.";
      setErr(msg);
      setStreets([]);
    } finally {
      setLoading(false);
    }
  }, [cityId]);

  useEffect(() => {
    fetchCityMeta();
    fetchStreets();
  }, [fetchCityMeta, fetchStreets]);

  return (
    <div className={cls.wrap}>
      <header className={cls.header}>
        <div>
          {/* ✅ Back to streets */}
          {cityId && (
            <Link className={cls.backBtn} to={`/admin/cities/single/${cityId}`}>
              ← Back To{" "}
              {cityMeta?.cityName ? `${cityMeta.cityName}` : "Streets"}
            </Link>
          )}
          <p className={cls.eyebrow}>Admin · Streets</p>
          <h1 className="nu-title is-mr" data-glow="1">
            {cityMeta?.cityName ? `Streets · ${cityMeta.cityName}` : "Streets"}
          </h1>
        </div>
        <div className={cls.actions}>
          <button
            className="nu-btn nu-btn--ghost"
            type="button"
            onClick={fetchStreets}
          >
            Refresh
          </button>
          {/* + Create Street – note the cityId from params */}
          {cityId && (
            <Link
              className="nu-btn nu-btn--primary"
              to={`/admin/cities/single/${cityId}/streets/create`}
            >
              + Create Street
            </Link>
          )}
        </div>
      </header>

      {err && <div className={cls.error}>⚠ {err}</div>}

      <div className={cls.grid} aria-busy={loading ? "true" : "false"}>
        {loading ? (
          [...Array(6)].map((_, i) => (
            <div key={`sk-${i}`} className={`${cls.card} ${cls.skeleton}`}>
              <div className={cls.skelThumb} />
              <div className={cls.skelLine} />
              <div className={cls.skelLineWide} />
            </div>
          ))
        ) : streets.length === 0 ? (
          <div className={cls.empty}>
            No streets yet. Create the first one for this city.
          </div>
        ) : (
          streets.map((street) => (
            <article key={street._id} className={cls.card}>
              <div className={cls.thumbWrap}>
                <img
                  src={getImg(street)}
                  alt={street.streetName || "Street avatar"}
                  className={cls.thumb}
                />
                <div className={cls.status} data-status={street.streetStatus}>
                  {street.streetStatus || "unknown"}
                </div>
              </div>

              <div className={cls.meta}>
                <div className={cls.titleRow}>
                  <h3>{street.streetName || "Untitled"}</h3>
                  <span className={cls.lang}>
                    {street.streetLanguage?.toUpperCase?.() || "EN"}
                  </span>
                </div>
                <p className={cls.sub}>{street.streetVibe || "No vibe set."}</p>
                <p className={cls.updated}>
                  Updated {formatDate(street.updatedAt)}
                </p>
              </div>

              <div className={cls.actionsRow}>
                {/* Clubs button pinned LEFT */}
                <Link
                  className="nu-btn nu-btn--green"
                  style={{ marginRight: "auto" }}
                  to={`/admin/cities/single/${cityId}/streets/${street._id}/clubs`}
                >
                  Clubs
                </Link>

                {/* View button stays RIGHT */}
                <Link
                  className="nu-btn nu-btn--ghost"
                  to={`/admin/cities/single/${cityId}/streets/${street._id}`}
                >
                  View
                </Link>
              </div>
            </article>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminStreets;
